import { httpClient } from "@coworker/http-client";
import { Articles } from "../types/article";
import {
  PartialMedia,
  MediaResponse,
  MediasAndArticleCountResponse,
  MediasResponse,
  MediaId,
  Notifications,
} from "../types/media";
import { ensureNotificationsIntegrity } from "./medias.service.helper";

export const getMediasByStoreId = async (storeId: string) => {
  const data = await httpClient.roomsettings.get<MediasResponse>(
    `/medias/v3/stores/${encodeURIComponent(storeId)}`
  );
  data.medias = data.medias.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { numeric: true })
  );
  return data;
};

export const getArticlesByMediaId = async (mediaId?: string | undefined) => {
  if (!mediaId) return [];
  const data = await httpClient.roomsettings.get<{ articles: Articles }>(
    `/medias/${mediaId}/articles`
  );
  return data.articles;
};

export const getArticlesByProductArticleId = async (
  mediaId: string,
  productArticleId: string
) => {
  try {
    return await httpClient.roomsettings.get<Articles>(
      `/medias/${mediaId}/articles/product_article_id/${productArticleId}`
    );
  } catch (error: unknown) {
    // 404 means no articles found — return empty array
    if (
      error &&
      typeof error === "object" &&
      "status" in error &&
      (error as { status: number }).status === 404
    ) {
      return [];
    }
    console.error("Fetch error:", error);
    return [];
  }
};

export const getMediaById = async (
  mediaId: string
): Promise<MediaResponse | null> => {
  try {
    if (!mediaId) return null;
    return await httpClient.roomsettings.get<MediaResponse>(
      `/medias/${mediaId}`
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getMediasAndArticleCount = async (
  productArticleId: string,
  storeId: string
) => {
  try {
    const data = await httpClient.roomsettings.get<{
      mediasAndArticleCount: MediasAndArticleCountResponse[];
    }>(`/medias/${encodeURIComponent(storeId)}/articles/${productArticleId}`);
    return data.mediasAndArticleCount;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const createMedia = async (mediaData: PartialMedia) => {
  // Make sure id is not set. Should be generated in service.
  delete mediaData.id;
  return await httpClient.roomsettings.post<MediaId>("/medias", {
    body: mediaData,
  });
};

export const updateMedia = async (mediaId: string, mediaData: PartialMedia) => {
  try {
    return await httpClient.roomsettings.put<string>(`/medias/${mediaId}`, {
      body: mediaData,
    });
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const deleteMedia = async (mediaId: string) => {
  try {
    return await httpClient.roomsettings.delete<string>(`/medias/${mediaId}`);
  } catch (error) {
    console.error("Delete error:", error);
  }
  return null;
};

export const getNotifications = async (storeId: string, countryId: string) => {
  try {
    const notifications = await httpClient.roomsettings.get<Notifications>(
      `/medias/${encodeURIComponent(storeId)}/notifications`,
      { params: { countryId } }
    );
    ensureNotificationsIntegrity(notifications);

    return notifications;
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return { alerts: [], taskLinks: [], uniqueEdsDates: [] } as Notifications;
};
